/**
 * Backing rollup — "N ratified · N backed · N announced only", plus
 * the announced-only list, on the Plan of Record dashboard (D1).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md §4.
 * Judgment: src/lib/commitmentState.ts. Assembly: commitmentStateService.ts.
 *
 * The announced-only list is the point of the surface: it is Ng'andu's
 * ranked-but-unresourced gap made visible in a customer's own data. In
 * production today it is every commitment on the record, and the card says why
 * rather than letting a correct zero read as a broken feature.
 *
 * Record-scoped, so it gates on the RECORD's workspace, not the session's.
 * A cycle-less org-level record renders nothing: resource claims key on
 * (plan_cycle_id, item_id), so there is no ledger to read.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssembledPor } from '../../types/plan-of-record';
import { HOURS_PER_CAPACITY_UNIT } from '../../lib/itemToPlanningAdapter';
import {
  EMPTY_COMMITMENT_STATES,
  listCommitmentItemIdsForCycle,
  loadCommitmentStatesForCycle,
  type CommitmentStatesForCycle,
} from '../../lib/commitmentStateService';
import { COMMITMENT_STATE_LABELS, type CommitmentStateView } from '../../lib/commitmentState';
import { useCommitmentStatesEnabledForWorkspace } from '../../hooks/useCommitmentStatesEnabled';
import { PorCard } from './porUi';

/** How many announced-only rows to name before collapsing to a count. */
const NAMED_ANNOUNCED_LIMIT = 8;

export function CommitmentStateRollup({ assembled }: { assembled: AssembledPor }) {
  const { record } = assembled;
  const enabled = useCommitmentStatesEnabledForWorkspace(record.workspace_id);
  const planCycleId = record.plan_cycle_id;

  const [states, setStates] = useState<CommitmentStatesForCycle>(EMPTY_COMMITMENT_STATES);
  const [titles, setTitles] = useState<Map<string, string>>(new Map());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || !planCycleId) {
      setStates(EMPTY_COMMITMENT_STATES);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { itemIds, titleByItemId } = await listCommitmentItemIdsForCycle(planCycleId);
      if (cancelled) return;
      if (itemIds.length === 0) {
        setStates(EMPTY_COMMITMENT_STATES);
        setLoaded(true);
        return;
      }
      const result = await loadCommitmentStatesForCycle({
        planCycleId,
        itemIds,
        workspaceId: record.workspace_id,
        // No scenario is in scope on this surface; items store effort in
        // capacity units of 8h, the same fallback timePhasedPlanningService uses.
        defaultWorkUnitHours: HOURS_PER_CAPACITY_UNIT,
      });
      if (cancelled) return;
      setStates(result);
      // The plan version's own titles, with the record's linked commitment
      // titles layered on where a POR commitment names the work differently.
      setTitles(new Map([...titleByItemId, ...titlesFromRecord(assembled)]));
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, planCycleId, record.workspace_id, assembled]);

  if (!enabled || !planCycleId || !loaded) return null;
  if (states.rollup.total === 0) return null;

  const announced = [...states.byItemId.entries()].filter(([, v]) => v.state === 'announced');
  const pct = Math.round(states.rollup.committedShare * 100);

  return (
    <PorCard>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        Backing
      </div>
      <p className="text-lg font-heading font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-1 tabular-nums">
        {states.rollup.committed} of {states.rollup.total} commitment
        {states.rollup.total === 1 ? '' : 's'} backed
      </p>

      <div
        className="mt-2 h-1.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--hover-tint)] overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Commitments that are backed"
      >
        <div className="h-full bg-[var(--green)]" style={{ width: `${pct}%` }} />
      </div>

      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-2.5">
        {states.rollup.sentence}
      </p>
      {states.noDemandTemplates && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
          No work type in this workspace carries a demand template yet, so there is nothing to
          measure claims against — coverage stays unmeasurable until one does. Set one in{' '}
          <Link
            to="/prioritization/settings/work-types"
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            Settings → Work Types
          </Link>
          .
        </p>
      )}

      {announced.length > 0 && (
        <div className="mt-4 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-3">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--mustard)] dark:text-[var(--mustard)]">
            {COMMITMENT_STATE_LABELS.announced} only — decided, not backed
          </div>
          <ul className="mt-2 space-y-1.5">
            {announced.slice(0, NAMED_ANNOUNCED_LIMIT).map(([itemId, view]) => (
              <li key={itemId} className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <span className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {titles.get(itemId) ?? 'Untitled commitment'}
                </span>
                <span className="block text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {shortReason(view)}
                </span>
              </li>
            ))}
          </ul>
          {announced.length > NAMED_ANNOUNCED_LIMIT && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-2">
              and {announced.length - NAMED_ANNOUNCED_LIMIT} more.
            </p>
          )}
        </div>
      )}
    </PorCard>
  );
}

/**
 * POR commitment titles for items the record explicitly links. Only 1 of 13
 * commitments in production carries a link today, so these layer OVER the plan
 * version's own initiative titles rather than replacing them.
 */
function titlesFromRecord(assembled: AssembledPor): Map<string, string> {
  const byItemId = new Map<string, string>();
  for (const c of assembled.commitments) {
    if (c.linked_initiative_id) byItemId.set(c.linked_initiative_id, c.title);
  }
  return byItemId;
}

function shortReason(view: CommitmentStateView): string {
  return view.sentence;
}
